'use client'

import { CloseIcon } from '@repo/ui/CloseIcon'
import { confirm } from '@repo/ui/Confirm'
import { OspreyBankAccountSelect } from '@repo/ui/OspreyBankAccountSelect'
import { Portal } from '@mui/base/Portal'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useEffect, useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIBackdrop from '@mui/material/Backdrop'
import MUIButton from '@mui/material/Button'
import MUIFormControl from '@mui/material/FormControl'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'

type RowState = { name: string; ospreyName: string; ospreyBankAccount: any }

export const BankFeedsAddNewFeed = ({
  bankFeed,
  editMode = false,
  isOpen,
  metadata,
  setMetadata,
  setOpen,
  setToken,
  testData = false,
  token
}: BankFeedsAddNewFeedProps) => {
  const theme = useTheme()
  const {
    bankFeeds,
    fetchAllBankFeeds,
    removeOspreyBankAccountFromList,
    resetBankFeedsHaveChanged,
    resetOspreyBankAccounts,
    saveNewBankFeeds,
    setBankFeedsHaveChanged,
    setSingleBankFeed,
    updateBankFeed
  } = useBankFeedsStore()

  const [newOspreyName, setNewOspreyName] = useState(bankFeed?.ospreyName ?? '')
  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [saveDisabled, setSaveDisabled] = useState(true)

  const getKey = (acct: any, idx: number) => acct?.account_id ?? acct?.id ?? String(idx)

  const getOspreyId = (v: any) => {
    if (v == null || testData) return null
    if (typeof v === 'string' || typeof v === 'number') return String(v)
    return v.ospreyBankAccountId ?? v.osprey_bank_account_id ?? v.bankAccountId ?? v.id ?? v.value ?? null
  }

  const hasNonBlankId = (v: any) => {
    const id = getOspreyId(v)
    return id != null && String(id).trim() !== ''
  }

  const handleClose = async () => {
    const proceed = editMode
      ? await confirm('Your bank feed name will not be updated if you leave now. Are you sure you want to leave?')
      : await confirm(
          'Your bank feed will not be linked to an Osprey bank account if you leave now. Are you sure you want to leave?'
        )
    if (!proceed) return

    await resetOspreyBankAccounts()
    await resetBankFeedsHaveChanged()
    setMetadata && setMetadata(null)
    setToken && setToken(null)
    setOpen(false)
  }

  const handleSave = async () => {
    if (testData) return

    if (editMode) {
      const updatedBankFeedData = {
        accountType: bankFeed.accountType,
        mask: bankFeed.mask,
        name: bankFeed.name,
        ospreyName: newOspreyName,
        isoCurrencyCode: bankFeed.isoCurrencyCode,
        plaidBankAccountId: bankFeed.plaidBankAccountId,
        plaidInstitutionId: bankFeed.plaidInstitutionId,
        subType: bankFeed.subType
      }

      try {
        await updateBankFeed({ bankFeed: updatedBankFeedData })

        await fetchAllBankFeeds()

        if (Array.isArray(bankFeeds)) {
          const match = bankFeeds.find((b) => String(b.plaidBankAccountId) === bankFeed.plaidBankAccountId) ?? null
          setSingleBankFeed({ bankFeed: match })
        }

        await setBankFeedsHaveChanged()
        setOpen(false)
      } catch (error) {
        console.error('Error updating bank feed:', error)
      }
    } else {
      try {
        await saveNewBankFeeds({
          institutionId: metadata?.institution?.institution_id,
          institutionName: metadata?.institution?.name,
          plaidBankAccountMappings: readyBankFeeds,
          publicToken: token
        })

        await setBankFeedsHaveChanged()
        setMetadata && setMetadata(null)
        setToken && setToken(null)
        setOpen(false)
      } catch (error) {
        console.error('Error saving new bank feeds:', error)
        return
      }
    }
  }

  useEffect(() => {
    if (!isOpen || !metadata?.accounts || testData) return
    setRows((prev) => {
      const next = { ...prev }
      metadata.accounts.forEach((acct: any, idx: number) => {
        const key = getKey(acct, idx)
        if (!next[key]) next[key] = { ospreyName: acct?.name ?? '', name: acct?.name ?? '', ospreyBankAccount: null }
      })
      return next
    })
  }, [isOpen, metadata])

  const newBankFeedObjects = useMemo(() => {
    const accounts = metadata?.accounts ?? []
    return accounts.map((acct: any, idx: number) => {
      const key = getKey(acct, idx)
      const row = rows[key] ?? { name: acct?.name ?? '', ospreyName: acct?.name ?? '', ospreyBankAccount: null }
      const effectiveOspreyName = row.ospreyName?.trim() ? row.ospreyName.trim() : null

      return {
        accountId: acct?.id ?? null,
        accountType: acct?.type ?? null,
        mask: acct?.mask ?? null,
        name: acct?.name ?? row.name ?? '',
        ospreyBankAccountId: getOspreyId(row.ospreyBankAccount),
        ospreyName: effectiveOspreyName,
        subType: acct?.subtype ?? null
      }
    })
  }, [metadata, rows])

  const readyBankFeeds = useMemo(
    () =>
      newBankFeedObjects.filter(
        (r: any) => r.ospreyBankAccountId != null && String(r.ospreyBankAccountId).trim() !== ''
      ),
    [newBankFeedObjects]
  )

  const allSelected = useMemo(() => {
    const accounts = metadata?.accounts ?? []
    return accounts.every((acct: any, idx: number) => {
      const key = getKey(acct, idx)
      return hasNonBlankId(rows[key]?.ospreyBankAccount)
    })
  }, [metadata?.accounts, rows])

  useEffect(() => {
    setSaveDisabled(!allSelected)
  }, [allSelected])

  return (
    <>
      <MUIPaper
        data-testid="BankFeedsAddNewFeed"
        elevation={20}
        sx={{
          height: 'fit-content',
          margin: '20px',
          minHeight: '288px',
          maxWidth: '455px',
          opacity: isOpen ? 1 : 0,
          top: isOpen ? '10%' : '0',
          left: isOpen ? 'calc(50% - 227.5px)' : '0',
          pb: '20px',
          pointerEvents: isOpen ? 'auto' : 'none',
          position: 'fixed',
          visibility: isOpen ? 'visible' : 'hidden',
          width: '100%',
          zIndex: 910
        }}
      >
        <MUIStack direction="row" justifyContent="space-between" alignItems="center" sx={{ m: '1.5rem 1.5rem 1rem' }}>
          <MUITypography variant="h4" sx={{ m: 0 }}>
            {editMode ? `Edit bank feed` : `Add new bank feed`}
          </MUITypography>
          <MUIButton onClick={handleClose} variant="text" sx={{ minWidth: 16, height: 16, width: 16, p: 0 }}>
            <CloseIcon sx={{ height: 16, width: 16 }} />
          </MUIButton>
        </MUIStack>
        <hr
          style={{
            width: 'calc(100% - 3rem)',
            margin: '0 1.5rem',
            padding: 0,
            border: '1px solid rgba(5, 28, 51, 0.06)'
          }}
        />

        <MUIStack
          direction="column"
          alignItems="center"
          sx={{ maxHeight: '600px', overflowY: metadata?.accounts?.length > 3 ? 'scroll' : 'none' }}
        >
          {metadata ? (
            metadata?.accounts?.map((acct: any, idx: number) => {
              const key = getKey(acct, idx)
              const row = rows[key] ?? { name: '', ospreyBankAccount: null }

              return (
                <MUIFormControl key={key} sx={{ mt: '1.5rem', width: '100%', maxWidth: '455px' }}>
                  <MUIStack direction="column" alignItems="center" gap={4} sx={{ width: '100%', maxWidth: '455px' }}>
                    <MUITypography variant="body2" sx={{ width: '100%', maxWidth: 375, fontWeight: '600' }}>
                      {acct?.name ?? 'Account'} &middot; {acct?.subtype ?? acct?.type ?? '—'}
                      {acct?.mask ? ` · **** ${acct.mask}` : ''}
                    </MUITypography>

                    <MUITextField
                      label="Name"
                      variant="outlined"
                      value={row.ospreyName ?? ''}
                      onChange={(e) =>
                        setRows((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], ospreyName: e.target.value }
                        }))
                      }
                      sx={{
                        backgroundColor: theme.palette.primary.contrastText,
                        width: '100%',
                        maxWidth: 375,
                        '& .MuiInputBase-root': { height: 40, mb: 0 },
                        '& .MuiFormLabel-root': { top: -5 },
                        "& > label[data-shrink='true']": { top: 0, m: 0 }
                      }}
                    />

                    <OspreyBankAccountSelect
                      value={row.ospreyBankAccount}
                      onChange={(val: any) => {
                        setRows((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], ospreyBankAccount: val }
                        }))
                        removeOspreyBankAccountFromList({ ospreyBankAccountId: val.bankAccountId })
                      }}
                      sx={{ maxWidth: 375, width: '100%', m: 0 }}
                    />
                  </MUIStack>
                </MUIFormControl>
              )
            })
          ) : (
            <MUIFormControl sx={{ mt: '1.5rem', width: '100%', maxWidth: '455px' }}>
              <MUIStack direction="column" alignItems="center" gap={4} sx={{ width: '100%', maxWidth: '455px' }}>
                <MUITypography variant="body2" sx={{ width: '100%', maxWidth: 375, opacity: 0.8 }}>
                  {bankFeed?.name ?? 'Account'} &middot; {bankFeed?.subtype ?? bankFeed?.accountType ?? '—'}
                  {bankFeed?.mask ? ` · **** ${bankFeed.mask}` : ''}
                </MUITypography>

                <MUITextField
                  label="Name"
                  variant="outlined"
                  value={newOspreyName}
                  onChange={(e) => setNewOspreyName(e.target.value)}
                  sx={{
                    backgroundColor: theme.palette.primary.contrastText,
                    width: '100%',
                    maxWidth: 375,
                    '& .MuiInputBase-root': { height: 40, mb: 0 },
                    '& .MuiFormLabel-root': { top: -5 },
                    "& > label[data-shrink='true']": { top: 0, m: 0 }
                  }}
                />
              </MUIStack>
            </MUIFormControl>
          )}
        </MUIStack>

        <MUIStack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ mt: '2rem', mr: '2rem', gap: '1rem' }}
        >
          <MUIButton onClick={handleClose} variant="text">
            Cancel
          </MUIButton>
          <MUIButton
            variant="contained"
            onClick={handleSave}
            disabled={saveDisabled}
            aria-label={!saveDisabled ? '' : 'Select at least one Osprey account for each bank account'}
          >
            Save
          </MUIButton>
        </MUIStack>
      </MUIPaper>

      <Portal>
        <MUIBackdrop open={isOpen} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 900 }} />
      </Portal>
    </>
  )
}

type BankFeedsAddNewFeedProps = {
  bankFeed?: any
  editMode?: boolean
  isOpen: boolean
  metadata?: any
  setMetadata?: (metadata: any) => void
  setOpen: (isOpen: boolean) => void
  setToken?: (token: string | null) => Promise<void>
  testData?: boolean
  token?: string | null
}
